/*
 * Header menus (sections/header.liquid), loaded by the header section:
 *   <header-navigation>  desktop navigation: submenus open to the left when they would overflow,
 *                        only one mega menu at a time
 *   (document-wide)      touch devices: first tap on a .has-sub-menu link opens its dropdown
 *   <header-mobile-menu> mobile menu: toggle, expandable items, Escape/outside click/anchor link close
 *   <cdx-search-trigger> mobile search icon: focuses/clicks the header search input (Doofinder hooks it)
 * Uses trapFocus/removeTrapFocus from assets/scripts.js.
 */
if (!customElements.get("header-navigation")) {
  const isTouchDevice = () => "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isAnchorLink = (element) =>
    element.tagName === "A" && /^#[\w-]+/.test(element.getAttribute("href") || "");

  customElements.define(
    "header-navigation",
    class HeaderNavigation extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.addEventListener("click", (event) => this.onClick(event), { signal });

        this.setMenuClasses();
        window.addEventListener(
          "resize",
          () => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => this.setMenuClasses(), 350);
          },
          { signal }
        );

        this.querySelectorAll(".navigation-main").forEach((menu) => this.setupSingleMegaMenu(menu, signal));
        document.addEventListener(
          "visibilitychange",
          () => {
            if (document.hidden) this.closeAllMenus();
          },
          { signal }
        );
        window.addEventListener("blur", () => this.closeAllMenus(), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        clearTimeout(this.resizeTimer);
      }

      onClick(event) {
        const { target } = event;

        // In-page anchor in the mega menu: close the menus so the target is visible
        const mainMenu = target.closest(".navigation-main");
        if (mainMenu && isAnchorLink(target)) {
          mainMenu.classList.add("menus-closed");
          setTimeout(() => mainMenu.classList.remove("menus-closed"), 100);
        }
      }

      // Submenus open to the left when they would overflow the window
      setMenuClasses() {
        const getMenuWidth = (element, width = 0) => {
          const submenu = element && element.querySelector(".navigation-sub-menu");
          return submenu ? getMenuWidth(submenu, width + submenu.clientWidth) : width;
        };

        this.querySelectorAll(".navigation-menu > .has-sub-menu").forEach((item) => {
          if (item.classList.contains("navigation-item-static")) return;
          item.classList.remove("nav-open-left");
          if (item.offsetLeft + getMenuWidth(item) > window.innerWidth) {
            item.classList.add("nav-open-left");
          }
        });
      }

      // Only one mega menu open at a time (hovering a top item hides the others' submenus)
      setupSingleMegaMenu(menu, signal) {
        const topItems = menu.querySelectorAll(":scope > .navigation-item");
        const setSubmenuHidden = (item, hidden) => {
          const submenu = item.querySelector(":scope > .navigation-sub-menu");
          if (!submenu) return;
          submenu.style.visibility = hidden ? "hidden" : "";
          submenu.style.opacity = hidden ? "0" : "";
          submenu.style.pointerEvents = hidden ? "none" : "";
        };

        topItems.forEach((item) => {
          item.addEventListener(
            "mouseenter",
            () => {
              topItems.forEach((other) => setSubmenuHidden(other, other !== item));
            },
            { signal }
          );
          item.addEventListener("mouseleave", () => setSubmenuHidden(item, false), { signal });
        });

        // Right-click (e.g. "open in new tab") closes the menus
        menu.addEventListener("contextmenu", () => setTimeout(() => this.closeAllMenus(), 200), { signal });
      }

      closeAllMenus() {
        this.querySelectorAll(".navigation-main").forEach((menu) => {
          menu.classList.add("menus-closed");
          setTimeout(() => {
            menu.classList.remove("menus-closed");
            menu.querySelectorAll(":scope > .navigation-item > .navigation-sub-menu").forEach((submenu) => {
              submenu.style.visibility = "";
              submenu.style.opacity = "";
              submenu.style.pointerEvents = "";
            });
          }, 150);
        });
      }
    }
  );

  customElements.define(
    "header-mobile-menu",
    class HeaderMobileMenu extends HTMLElement {
      connectedCallback() {
        this.wrapper = this.querySelector(".head-slot-nav-mobile-link-wrapper");
        this.toggleButton = this.querySelector(".head-slot-nav-mobile-link");
        this.menu = this.querySelector(".mobile-menu");
        this.nav = this.querySelector(".navigation-mobile-wrap");
        if (!this.wrapper || !this.toggleButton || !this.menu) return;
        this.expandOnButton = this.menu.dataset.buttonExpand != null;

        this.abortController = new AbortController();
        const { signal } = this.abortController;
        document.body.addEventListener("click", (event) => this.onBodyClick(event), { signal });
        this.wrapper.addEventListener(
          "keyup",
          (event) => {
            if (event.code?.toUpperCase() === "ESCAPE") this.close();
          },
          { signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.isOpen) document.body.classList.remove("mobile-menu-open");
      }

      get isOpen() {
        return document.body.classList.contains("mobile-menu-open");
      }

      onBodyClick(event) {
        const { target } = event;

        // Expand items with a submenu (whole item, or only its arrow button)
        const item = target.closest(".navigation-mobile-item");
        if (item && this.menu.contains(item) && item.querySelector("ul")) {
          if (!this.expandOnButton) {
            event.preventDefault();
            item.classList.toggle("menu-item-expanded");
          } else if (target.closest(".navigation-mobile-item-link-expand")) {
            item.classList.toggle("menu-item-expanded");
          }
        }

        if (target.closest(".head-slot-nav-mobile-link") === this.toggleButton) {
          if (this.isOpen) this.close();
          else this.open();
          return;
        }

        if (isAnchorLink(target)) this.close();

        if (this.isOpen && !target.closest(".mobile-menu")) {
          event.preventDefault();
          this.close();
        }
      }

      open() {
        document.body.classList.add("mobile-menu-open");
        this.toggleButton.setAttribute("aria-expanded", "true");
        this.menu.setAttribute("aria-hidden", "false");
        trapFocus(this.wrapper, this.nav);
        const header = document.querySelector(".header");
        if (header) {
          this.menu.style.maxHeight = `${window.innerHeight - header.getBoundingClientRect().bottom + 70}px`;
        }
        document.dispatchEvent(new CustomEvent("mobile-menu:open"));
      }

      close() {
        if (!this.isOpen) return;
        document.body.classList.remove("mobile-menu-open");
        this.toggleButton.setAttribute("aria-expanded", "false");
        this.menu.setAttribute("aria-hidden", "true");
        removeTrapFocus(this.toggleButton);
        this.menu.style.maxHeight = "";
        document.dispatchEvent(new CustomEvent("mobile-menu:close"));
      }
    }
  );

  // Touch devices: the first tap on a menu item with a submenu opens it, the second follows the link
  // (header navigation and the collection side menu)
  document.addEventListener("click", (event) => {
    const link = event.target.closest(".has-sub-menu > a");
    if (!link || !isTouchDevice()) return;
    document.querySelectorAll(".has-sub-menu > a.is-dropdown-open").forEach((other) => {
      if (other !== link) other.classList.remove("is-dropdown-open");
    });
    if (!link.classList.contains("is-dropdown-open")) {
      event.preventDefault();
      link.classList.add("is-dropdown-open");
    }
  });

  customElements.define(
    "cdx-search-trigger",
    class CdxSearchTrigger extends HTMLElement {
      connectedCallback() {
        this.onClick = (event) => {
          event.preventDefault();
          const input = document.getElementById(this.getAttribute("target") || "header-global-search-input");
          if (!input) return;
          input.focus({ preventScroll: true });
          input.click();
        };
        this.addEventListener("click", this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
      }
    }
  );
}
